import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

let _selUid = 0;

@Component({
  selector: 'tc-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './select.component.html',
  styleUrl: './select.component.css',
})
export class SelectComponent {
  private readonly elRef = inject(ElementRef);

  value = input<string>('');
  options = input<string[]>([]);
  placeholder = input<string>('— nevybráno —');
  ariaLabel = input<string>('');
  valueChange = output<string>();

  open = signal(false);
  highlighted = signal(-1);
  dropdownTop = signal<number | null>(null);
  dropdownBottom = signal<number | null>(null);
  dropdownRight = signal(0);
  dropdownMinWidth = signal(180);

  readonly listId = `tc-sel-list-${_selUid++}`;
  optId(i: number): string {
    return `${this.listId}-opt-${i}`;
  }

  displayValue = computed(() => this.value() || this.placeholder());

  toggle(): void {
    if (this.open()) this.open.set(false);
    else this.openDropdown();
  }

  select(opt: string): void {
    this.valueChange.emit(opt);
    this.open.set(false);
  }

  onKeydown(e: KeyboardEvent): void {
    if (!this.open()) {
      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowUp' ||
        e.key === 'Enter' ||
        e.key === ' '
      ) {
        e.preventDefault();
        this.openDropdown();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.move(-1);
        break;
      case 'Home':
        e.preventDefault();
        this.highlighted.set(0);
        this.scrollToHighlighted();
        break;
      case 'End':
        e.preventDefault();
        this.highlighted.set(this.options().length - 1);
        this.scrollToHighlighted();
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const opt = this.options()[this.highlighted()];
        if (opt !== undefined) this.select(opt);
        break;
      }
      case 'Escape':
        e.preventDefault();
        this.open.set(false);
        break;
      case 'Tab':
        this.open.set(false);
        break;
    }
  }

  private openDropdown(): void {
    this.updatePosition();
    const idx = this.options().indexOf(this.value());
    this.highlighted.set(idx >= 0 ? idx : 0);
    this.open.set(true);
    setTimeout(() => this.scrollToHighlighted(), 0);
  }

  private move(delta: number): void {
    const n = this.options().length;
    if (!n) return;
    this.highlighted.update((cur) => (cur + delta + n) % n);
    this.scrollToHighlighted();
  }

  private updatePosition(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.sel-trigger',
    ) as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 120 && rect.top > spaceBelow;

    if (openUpward) {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(window.innerHeight - rect.top + 4);
    } else {
      this.dropdownBottom.set(null);
      this.dropdownTop.set(rect.bottom + 4);
    }
    this.dropdownRight.set(window.innerWidth - rect.right);
    this.dropdownMinWidth.set(Math.max(rect.width, 180));
  }

  private scrollToHighlighted(): void {
    const dropdown = this.elRef.nativeElement.querySelector(
      '.sel-dropdown',
    ) as HTMLElement | null;
    if (!dropdown) return;
    const hl = dropdown.querySelector('.hl') as HTMLElement | null;
    if (hl) hl.scrollIntoView({ block: 'nearest' });
  }

  @HostListener('document:scroll')
  onDocScroll(): void {
    if (this.open()) this.updatePosition();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (
      this.open() &&
      !this.elRef.nativeElement.contains(e.target as HTMLElement)
    ) {
      this.open.set(false);
    }
  }
}
